import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    TablePagination
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Add as AddIcon
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { APIEndpointOut, APIEndpointCreate, APIEndpointUpdate } from 'src/api/Api';
import { useSnackbar } from 'src/view/components/SnackbarService';
import { formatDate } from 'src/util/date-utils';

const ApiEndpoints: React.FC = () => {
    const [endpoints, setEndpoints] = useState<APIEndpointOut[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const { showSnackbar, snackbarType } = useSnackbar();

    // Dialog states
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedEndpoint, setSelectedEndpoint] = useState<APIEndpointOut | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());

    // Form states
    const [formData, setFormData] = useState<APIEndpointCreate>({
        service_name: '',
        path: '',
        method: 'GET',
        description: '',
        enabled: true
    });

    const fetchEndpoints = React.useCallback(async () => {
        setLoading(true);
        try {
            const response = await clientService.api.listApiEndpointsApiV1ApiEndpointsGet({
                page: page + 1,
                per_page: rowsPerPage,
                sort_by: 'created_at',
                sort_order: 'desc'
            });
            if (response.ok) {
                setEndpoints(response.data.endpoints);
                setTotalCount(response.data.count);
            }
        } catch (error) {
            console.error('Failed to fetch API endpoints:', error);
            showSnackbar({ message: 'Failed to fetch API endpoints', type: snackbarType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [page, rowsPerPage]);

    useEffect(() => {
        fetchEndpoints();
    }, [fetchEndpoints]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleCreateOpen = () => {
        setFormData({
            service_name: '',
            path: '',
            method: 'GET',
            description: '',
            enabled: true
        });
        setCreateDialogOpen(true);
    };

    const handleEditOpen = (endpoint: APIEndpointOut) => {
        setSelectedEndpoint(endpoint);
        setFormData({
            service_name: endpoint.service_name,
            path: endpoint.path,
            method: endpoint.method,
            description: endpoint.description || '',
            enabled: endpoint.enabled
        });
        setEditDialogOpen(true);
    };

    const handleDeleteOpen = (endpoint: APIEndpointOut) => {
        setSelectedEndpoint(endpoint);
        setDeleteDialogOpen(true);
    };

    const handleCreateSubmit = async () => {
        setActionLoading(true);
        try {
            const response = await clientService.api.createApiEndpointApiV1ApiEndpointsPost(formData);
            if (response.ok) {
                showSnackbar({ message: 'API Endpoint created successfully', type: snackbarType.SUCCESS });
                fetchEndpoints();
                setCreateDialogOpen(false);
            }
        } catch (error) {
            console.error('Failed to create API endpoint:', error);
            showSnackbar({ message: 'Failed to create API endpoint', type: snackbarType.ERROR });
        } finally {
            setActionLoading(false);
        }
    };

    const handleEditSubmit = async () => {
        if (!selectedEndpoint) return;
        setActionLoading(true);
        try {
            const updateData: APIEndpointUpdate = {
                service_name: formData.service_name,
                path: formData.path,
                method: formData.method,
                description: formData.description,
                enabled: formData.enabled
            };
            const response = await clientService.api.updateApiEndpointApiV1ApiEndpointsEndpointIdPut(selectedEndpoint.id, updateData);
            if (response.ok) {
                showSnackbar({ message: 'API Endpoint updated successfully', type: snackbarType.SUCCESS });
                fetchEndpoints();
                setEditDialogOpen(false);
            }
        } catch (error) {
            console.error('Failed to update API endpoint:', error);
            showSnackbar({ message: 'Failed to update API endpoint', type: snackbarType.ERROR });
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteSubmit = async () => {
        if (!selectedEndpoint) return;
        setActionLoading(true);
        try {
            const response = await clientService.api.deleteApiEndpointApiV1ApiEndpointsEndpointIdDelete(selectedEndpoint.id);
            if (response.ok) {
                showSnackbar({ message: 'API Endpoint deleted successfully', type: snackbarType.SUCCESS });
                fetchEndpoints();
                setDeleteDialogOpen(false);
            }
        } catch (error) {
            console.error('Failed to delete API endpoint:', error);
            showSnackbar({ message: 'Failed to delete API endpoint', type: snackbarType.ERROR });
        } finally {
            setActionLoading(false);
        }
    };

    const handleToggle = async (endpoint: APIEndpointOut) => {
        setTogglingIds(prev => new Set(prev).add(endpoint.id));
        try {
            const response = await clientService.api.toggleApiEndpointApiV1ApiEndpointsEndpointIdTogglePatch(endpoint.id, { enabled: !endpoint.enabled });
            if (response.ok) {
                showSnackbar({ message: `Endpoint ${!endpoint.enabled ? 'enabled' : 'disabled'} successfully`, type: snackbarType.SUCCESS });
                setEndpoints(prevEndpoints => 
                    prevEndpoints.map(ep => 
                        ep.id === endpoint.id ? { ...ep, enabled: !ep.enabled } : ep
                    )
                );
            }
        } catch (error) {
            console.error('Failed to toggle API endpoint:', error);
            showSnackbar({ message: 'Failed to toggle API endpoint', type: snackbarType.ERROR });
        } finally {
            setTogglingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(endpoint.id);
                return newSet;
            });
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">API Endpoints</Typography>
                <Box>
                    <Button 
                        startIcon={<RefreshIcon />} 
                        onClick={fetchEndpoints} 
                        variant="outlined"
                        disabled={loading}
                        sx={{ mr: 2 }}
                    >
                        Refresh
                    </Button>
                    <Button 
                        startIcon={<AddIcon />} 
                        onClick={handleCreateOpen} 
                        variant="contained"
                        color="primary"
                    >
                        Add Endpoint
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Service Name</TableCell>
                            <TableCell>Method</TableCell>
                            <TableCell>Path</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Created At</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : endpoints.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                    No API endpoints found
                                </TableCell>
                            </TableRow>
                        ) : (
                            endpoints.map((endpoint) => (
                                <TableRow key={endpoint.id}>
                                    <TableCell>{endpoint.service_name}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={endpoint.method} 
                                            size="small"
                                            color={
                                                endpoint.method === 'GET' ? 'info' :
                                                endpoint.method === 'POST' ? 'success' :
                                                endpoint.method === 'PUT' ? 'warning' :
                                                endpoint.method === 'DELETE' ? 'error' : 'default'
                                            }
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace' }}>{endpoint.path}</TableCell>
                                    <TableCell>{endpoint.description || '-'}</TableCell>
                                    <TableCell>
                                        <Tooltip title={endpoint.enabled ? "Disable" : "Enable"}>
                                            <Switch
                                                checked={endpoint.enabled}
                                                onChange={() => handleToggle(endpoint)}
                                                color="success"
                                                size="small"
                                                disabled={togglingIds.has(endpoint.id)}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{formatDate(endpoint.created_at)}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Edit">
                                            <IconButton onClick={() => handleEditOpen(endpoint)} color="primary" size="small">
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton onClick={() => handleDeleteOpen(endpoint)} color="error" size="small">
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Create/Edit Dialog */}
            <Dialog open={createDialogOpen || editDialogOpen} onClose={() => { setCreateDialogOpen(false); setEditDialogOpen(false); }} maxWidth="sm" fullWidth>
                <DialogTitle>{createDialogOpen ? 'Add API Endpoint' : 'Edit API Endpoint'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Service Name"
                            value={formData.service_name}
                            onChange={(e) => setFormData({ ...formData, service_name: e.target.value })}
                            fullWidth
                            required
                        />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl fullWidth required>
                                <InputLabel>Method</InputLabel>
                                <Select
                                    value={formData.method}
                                    label="Method"
                                    onChange={(e) => setFormData({ ...formData, method: e.target.value })}
                                >
                                    <MenuItem value="GET">GET</MenuItem>
                                    <MenuItem value="POST">POST</MenuItem>
                                    <MenuItem value="PUT">PUT</MenuItem>
                                    <MenuItem value="DELETE">DELETE</MenuItem>
                                    <MenuItem value="PATCH">PATCH</MenuItem>
                                </Select>
                            </FormControl>
                            <TextField
                                label="Path"
                                value={formData.path}
                                onChange={(e) => setFormData({ ...formData, path: e.target.value })}
                                fullWidth
                                required
                                placeholder="/api/v1/..."
                            />
                        </Box>
                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            fullWidth
                            multiline
                            rows={3}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.enabled ?? false}
                                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                                    color="success"
                                />
                            }
                            label="Enabled"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setCreateDialogOpen(false); setEditDialogOpen(false); }}>Cancel</Button>
                    <Button 
                        onClick={createDialogOpen ? handleCreateSubmit : handleEditSubmit} 
                        variant="contained" 
                        color="primary"
                        disabled={actionLoading || !formData.service_name || !formData.path || !formData.method}
                    >
                        {actionLoading ? <CircularProgress size={24} /> : (createDialogOpen ? 'Create' : 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete API Endpoint</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this endpoint? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteSubmit} color="error" variant="contained" disabled={actionLoading}>
                        {actionLoading ? <CircularProgress size={24} /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ApiEndpoints;
